import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import MobileSheetAutoComplete from '../../../components/ui/MobileSheetAutoComplete'
import { useEmployeeSearch } from '../../minutes/hooks/useEmployeeSearch'
import type { EmployeePick } from '../../minutes/types'
import { AGENDA_SOURCE } from '../api/agendaEmployeeSource'
import { personName, sicilKey, type AgendaPersonRef } from '../types'
import '../../minutes/minutes.css'

interface Props {
  /** İçteki arama kutusunun `id`'si — çağıran `<label htmlFor>` bağlar. */
  inputId: string
  value: AgendaPersonRef | null
  onChange: (next: AgendaPersonRef | null) => void
  /** Önerilmeyecek siciller (baştaki sıfırlar yok sayılır): davetteki herkes, görüntüleyen dahil. */
  exclude: ReadonlySet<string>
  placeholder: string
  invalid?: boolean
  disabled?: boolean
  /** Hata kutusunun `id`'si — içteki girdinin `aria-describedby`'ı. */
  describedBy?: string
}

/** Öneri satırı: ad + unvan + sicil. Modül seviyesinde — bileşen içinde tanımlı JSX fonksiyonu her
 *  çizimde yeniden yaratılırdı (Sonar S6478). */
function employeeItem(item: EmployeePick) {
  return (
    <div className="minutes-emp-item">
      <span className="minutes-emp-name">{item.fullName}</span>
      {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
      <span className="minutes-emp-sicil">{item.employeeId}</span>
    </div>
  )
}

/**
 * Yerime katılacak kişi seçicisi (131) — tek kişi. Davetliler alanıyla aynı arama ucu ve kişi kümesi;
 * davette zaten olan kimse önerilmez (sunucu da reddeder). Yeni seçim öncekinin yerine geçer.
 */
export default function SubstituteField({
  inputId,
  value,
  onChange,
  exclude,
  placeholder,
  invalid,
  disabled,
  describedBy,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { suggestions: raw, search } = useEmployeeSearch(AGENDA_SOURCE)
  const [query, setQuery] = useState('')
  // Referans sabit kalmalı (useMemo): AutoComplete her yeni `suggestions` referansında paneli yeniden açar.
  const suggestions = useMemo(
    () => raw.filter((s) => !exclude.has(sicilKey(s.employeeId))),
    [raw, exclude],
  )

  const complete = (e: AutoCompleteCompleteEvent) => search(e.query)

  // Yazarken string gelir; klavyeyle vurgulanan nesnede adı göster (seçim onSelect'te).
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v: unknown = e.value
    setQuery(typeof v === 'string' ? v : ((v as EmployeePick | null)?.fullName ?? ''))
  }

  const onSelect = (e: AutoCompleteSelectEvent) => {
    const picked = e.value as EmployeePick | null
    if (picked?.employeeId) {
      onChange({ employeeId: picked.employeeId, firstName: picked.firstName, lastName: picked.lastName })
    }
    setQuery('')
  }

  const name = value ? personName(value) : ''
  const removeLabel = t('agenda.response.removeSubstitute', { name })

  return (
    <div className="agenda-participants-field">
      <MobileSheetAutoComplete<EmployeePick>
        value={query}
        suggestions={suggestions}
        completeMethod={complete}
        field="fullName"
        delay={300}
        minLength={2}
        placeholder={placeholder}
        onChange={onQueryChange}
        onSelect={onSelect}
        inputId={inputId}
        disabled={disabled}
        panelClassName="minutes-participant-panel"
        className={invalid ? 'p-invalid minutes-participant-input' : 'minutes-participant-input'}
        itemTemplate={employeeItem}
        /* Tekil modda ARIA hedefi `input.root`'tur. */
        pt={{
          input: {
            root: { 'aria-invalid': invalid ? 'true' : 'false', 'aria-describedby': describedBy },
          },
        }}
      />
      {value && (
        <ul className="agenda-participant-chips">
          <li className="agenda-participant-chip">
            <span className="agenda-participant-name">{name}</span>
            <button
              type="button"
              className="agenda-participant-remove"
              aria-label={removeLabel}
              title={removeLabel}
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </li>
        </ul>
      )}
    </div>
  )
}
